import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { normalizeTicketStatusKey } from "../../../utils/ticketNormalization";
import { createApiClient, getTimezoneOffsetString, getLocalTimestamp } from "../../../api/apiClient";
import { formatTicketingDetailDateTime } from "../../../utils/ticketingDateTime";
import {
  EditableTicketInfo,
  SUBCATEGORY_OPTIONS,
  TicketDetailRecord,
  updateTicketStatus,
  setTriageInfo,
  TicketStatus,
  TicketUrgency,
  addAffectedLocation,
  removeAffectedLocation,
  addTicketStakeholderEmail,
  updateAffectedLocations,
  addTicketActivityComment,
  consolidateMergedTickets,
  ActivityItem,
} from "../../../store/slices/ticketingPortalSlice";
import {
  fetchTicketDetail,
  fetchTicketActivities,
  fetchSustainQueue,
  fetchMyTickets,
  postTicketComment,
  saveTicketDetailInfo,
  saveTicketInternalNote,
  fetchTicketNotes,
  updateTriageFields,
  updateAssignedTeam,
  addAffectedLocationToTicket,
  getAffectedLocationsForTicket,
  removeAffectedLocationFromTicket,
  addTicketDetailStakeholder,
  fetchAvailableTicketingTeams,
} from "../../../store/thunks/ticketingPortalThunk";
import { mergeMultipleTickets } from "../../../store/thunks/submitTicketThunk";
import { closeTicketWithRca } from "../util/closeTicketWithRca";

const FALLBACK_TICKET: TicketDetailRecord = {
  id: "NTI-2026-00421",
  title: "Loading ticket detail...",
  description: "",
  category: "System Issue",
  subcategory: "EWM / SAP",
  location: "SoCal (Market)",
  urgency: "high",
  status: "new",
  submitted: "",
  lastUpdated: "",
  submittedBy: "",
  market: "",
  ticketLevel: "Market",
  peopleImpacted: "",
  loadsImpacted: "",
  stakeholders: "",
  affectedLocations: [],
  notes: [],
  activity: [],
};

type ActionFeedback = {
  msg: string;
  type: "success" | "info" | "warning" | "error";
};

type EditErrorMap = Partial<Record<keyof EditableTicketInfo, string>>;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const nowAsTicketTime = (): string => formatTicketingDetailDateTime(new Date().toISOString());

export function useTicketDetail(ticketId?: string) {
  const dispatch = useDispatch<AppDispatch>();
  const {
    selectedTicketId,
    ticketDetails,
    sustainQueue,
    availableTicketingTeams,
  } = useSelector((state: RootState) => state.ticketingPortal);
  const currentUserEmail = useSelector((state: RootState) => (state as any).appData?.userInfo?.email ?? "");

  const resolvedTicketId = ticketId || selectedTicketId || "NTI-2026-00421";
  const ticket = ticketDetails[resolvedTicketId] || FALLBACK_TICKET;

  const savedInfo = useMemo<EditableTicketInfo>(
    () => ({
      category: ticket.category,
      subcategory: ticket.subcategory,
      tertiaryCategory: ticket.tertiaryCategory ?? "",
      ticketLevel: ticket.ticketLevel,
      market: ticket.market,
      urgency: ticket.urgency,
      peopleImpacted: ticket.peopleImpacted,
      loadsImpacted: ticket.loadsImpacted,
      stakeholders: ticket.stakeholders,
      description: ticket.description,
    }),
    [ticket]
  );

  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [editDraft, setEditDraft] = useState<EditableTicketInfo>(savedInfo);
  const [editErrors, setEditErrors] = useState<EditErrorMap>({});
  const [commentText, setCommentText] = useState("");
  const [noteText, setNoteText] = useState("");
  const [showAllLocations, setShowAllLocations] = useState(false);
  const [isWidespread, setIsWidespreadState] = useState(ticket.isWidespread || false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [isAdoModalOpen, setIsAdoModalOpen] = useState(false);
  const [isMergeSubmitting, setIsMergeSubmitting] = useState(false);
  const [impactedPickersCount, setImpactedPickersCount] = useState(0);

  // Triage update state
  const [resolutionPathDraft, setResolutionPathDraft] = useState(ticket.resolutionPath ?? "");
  const [assignedToDraft, setAssignedToDraft] = useState(ticket.assignedTo ?? "Unassigned");
  const [incidentNumberDraft, setIncidentNumberDraft] = useState(ticket.incidentId ?? "");
  const [isTriageSending, setIsTriageSending] = useState(false);

  // Assigned Team (Team Assignment Configuration auto-routing / manual reassignment)
  const [isReassigningTeam, setIsReassigningTeam] = useState(false);

  const refreshTeams = useCallback(async () => {
    await dispatch(fetchAvailableTicketingTeams());
  }, [dispatch]);

  const teams = useMemo(() => {
    const seenTeamNames = new Set<string>();
    return availableTicketingTeams.filter((team) => {
      const normalizedName = team.name.trim().toLowerCase();
      if (seenTeamNames.has(normalizedName)) return false;
      seenTeamNames.add(normalizedName);
      return true;
    });
  }, [availableTicketingTeams]);

  useEffect(() => {
    refreshTeams().catch(() => {});
  }, [refreshTeams]);

  const lastFetchedTicketIdRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    // Check if we need to fetch (either ticket ID changed or we haven't fetched this ticket yet)
    if (lastFetchedTicketIdRef.current === resolvedTicketId && isFetchingRef.current === false) {
      // Already fetched this ticket in this component lifecycle
      return;
    }

    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    console.log(`[useTicketDetail] Fetching fresh ticket data for ${resolvedTicketId}`);
    
    const fetchData = async () => {
      try {
        // Fetch ticket detail first, then fetch notes and activities after detail is loaded
        await dispatch(fetchTicketDetail(resolvedTicketId));
        console.log(`[useTicketDetail] Ticket detail loaded, now fetching notes and activities`);
        // Explicitly fetch activities to ensure they are always loaded, even for closed tickets
        dispatch(fetchTicketActivities(resolvedTicketId) as any);
        dispatch(fetchTicketNotes(resolvedTicketId) as any);
        lastFetchedTicketIdRef.current = resolvedTicketId;
      } finally {
        isFetchingRef.current = false;
      }
    };
    
    fetchData();
  }, [dispatch, resolvedTicketId]);

  useEffect(() => {
    // Detail fetch may resolve backendTicketId after initial mount; retrigger notes once ID exists.
    if (ticket.backendTicketId != null && ticket.id === resolvedTicketId) {
      console.log(`[useTicketDetail] backendTicketId resolved (${ticket.backendTicketId}), refetching notes`);
      dispatch(fetchTicketNotes(resolvedTicketId) as any);
    }
  }, [dispatch, resolvedTicketId, ticket.backendTicketId, ticket.id]);

  useEffect(() => {
    let mounted = true;

    const loadImpactedPickers = async () => {
      try {
        const response = await dispatch(getAffectedLocationsForTicket(resolvedTicketId) as any);
        if (mounted && response) {
          // Update affected locations with sapId data from the API response
          if (response.locations && Array.isArray(response.locations)) {
            dispatch(updateAffectedLocations({
              ticketId: resolvedTicketId,
              locations: response.locations,
            }));
          }
          const totalAffectedUsers = Number(response?.totalAffectedUsers);
          setImpactedPickersCount(Number.isFinite(totalAffectedUsers) ? totalAffectedUsers : 0);
        }
      } catch {
        if (mounted) {
          setImpactedPickersCount(0);
        }
      }
    };

    loadImpactedPickers();

    return () => {
      mounted = false;
    };
  }, [dispatch, resolvedTicketId, ticket.affectedLocations.length]);

  // Ensure the sustain queue is loaded so the Merge Tickets search has data
  // even when the user navigates directly to the ticket detail page.
  const sustainQueueLoaded = useSelector(
    (state: RootState) => state.ticketingPortal.sustainQueue.length > 0
  );
  useEffect(() => {
    if (!sustainQueueLoaded) {
      dispatch(fetchSustainQueue());
    }
  }, [dispatch, sustainQueueLoaded]);

  useEffect(() => {
    setEditDraft(savedInfo);
  }, [savedInfo]);

  useEffect(() => {
    setIsWidespreadState(Boolean(ticket.isWidespread));
  }, [ticket.id, ticket.isWidespread]);

  // Reset triage drafts when ticket changes
  useEffect(() => {
    setResolutionPathDraft(ticket.resolutionPath ?? "");
    setAssignedToDraft(ticket.assignedTo ?? "Unassigned");
    setIncidentNumberDraft(ticket.incidentId ?? "");
  }, [ticket.resolutionPath, ticket.assignedTo, ticket.incidentId, ticket.id]);

  function startEdit() {
    setEditDraft(savedInfo);
    setEditErrors({});
    setIsEditingInfo(true);
  }

  function cancelEdit() {
    setEditDraft(savedInfo);
    setEditErrors({});
    setIsEditingInfo(false);
  }

  function validateEditDraft(draft: EditableTicketInfo): EditErrorMap {
    const errors: EditErrorMap = {};

    if (!draft.category.trim()) {
      errors.category = "Category is required.";
    }
    if (!draft.subcategory.trim()) {
      errors.subcategory = "Sub-category is required.";
    } else {
      const validSubcategories = SUBCATEGORY_OPTIONS[draft.category] ?? [];
      if (validSubcategories.length > 0 && !validSubcategories.includes(draft.subcategory)) {
        errors.subcategory = "Please select a valid sub-category for the selected category.";
      }
    }
    if (!draft.ticketLevel.trim()) {
      errors.ticketLevel = "Ticket level is required.";
    }
    if (!draft.market.trim()) {
      errors.market = "Market / location is required.";
    }

    let stakeholderEmails: string[] = [];
    if (draft.stakeholders) {
      try {
        const parsed = JSON.parse(draft.stakeholders);
        stakeholderEmails = Array.isArray(parsed)
          ? parsed.map((item) => String(item).trim()).filter(Boolean)
          : [];
      } catch {
        stakeholderEmails = draft.stakeholders
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      }
    }
    if (stakeholderEmails.length > 10) {
      errors.stakeholders = "You can add up to 10 stakeholder emails.";
    } else if (stakeholderEmails.some((email) => !EMAIL_REGEX.test(email))) {
      errors.stakeholders = "Please enter valid stakeholder email addresses.";
    }

    if (draft.category === "System Issue") {
      if (!draft.peopleImpacted.trim()) {
        errors.peopleImpacted = "People impacted is required for System Issue tickets.";
      }
      if (!draft.loadsImpacted.trim()) {
        errors.loadsImpacted = "Loads impacted is required for System Issue tickets.";
      }
    }

    return errors;
  }

  function saveEdit(dynamicValues?: Array<{ fieldConfigId: number; historicalLabel: string; value: string }>) {
    const errors = validateEditDraft(editDraft);
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      return { msg: "Please fix highlighted fields before saving.", type: "error" } as const;
    }

    setEditErrors({});
    Promise.resolve(dispatch(saveTicketDetailInfo(resolvedTicketId, editDraft, dynamicValues) as any))
      .then(async () => {
        // Ensure ticket fields and impacted-location metrics are fresh after inline edit.
        await dispatch(fetchTicketDetail(resolvedTicketId) as any);
        const affectedResponse = await dispatch(getAffectedLocationsForTicket(resolvedTicketId) as any);
        if (affectedResponse) {
          // Update affected locations with sapId data from the API response
          if (affectedResponse.locations && Array.isArray(affectedResponse.locations)) {
            dispatch(updateAffectedLocations({
              ticketId: resolvedTicketId,
              locations: affectedResponse.locations,
            }));
          }
          const totalAffectedUsers = Number(affectedResponse?.totalAffectedUsers);
          setImpactedPickersCount(Number.isFinite(totalAffectedUsers) ? totalAffectedUsers : 0);
        }
      })
      .catch((error) => {
        console.warn("[useTicketDetail] Failed to refetch detail/affected locations after save:", error);
      });
    dispatch(
      addTicketActivityComment({
        ticketId: resolvedTicketId,
        activity: {
          id: `a${Date.now()}`,
          authorInitials: "SY",
          authorName: "System",
          role: "Auto",
          time: nowAsTicketTime(),
          content: "Ticket fields updated via inline edit.",
          type: "system",
        },
      })
    );
    setIsEditingInfo(false);
    return { msg: "Ticket details updated", type: "success" } as const;
  }

  function patchDraft(field: keyof EditableTicketInfo, value: string) {
    setEditDraft((prev) => ({ ...prev, [field]: value }));
    setEditErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  function submitComment() {
    dispatch(postTicketComment(resolvedTicketId, commentText));
    setCommentText("");
  }

  async function submitCommentFull(text: string, images: string[], actorRoleTag?: string): Promise<boolean> {
    return (dispatch(postTicketComment(resolvedTicketId, text, images, actorRoleTag)) as unknown as Promise<boolean>);
  }

  function submitNote() {
    dispatch(saveTicketInternalNote(resolvedTicketId, noteText));
    setNoteText("");
  }

  async function saveNoteFull(text: string, images: string[]): Promise<boolean> {
    return (dispatch(saveTicketInternalNote(resolvedTicketId, text, images)) as unknown as Promise<boolean>);
  }

  async function changeStatus(status: TicketStatus): Promise<ActionFeedback> {
    // Block status change if ticket is unassigned
    const effectiveAssignee = assignedToDraft || ticket.assignedTo || "Unassigned";
    if (effectiveAssignee === "Unassigned" && status !== "new") {
      return {
        msg: "Assign the ticket to a team member before changing its status.",
        type: "error",
      };
    }

    // Optimistically update local state
    dispatch(updateTicketStatus({ ticketId: resolvedTicketId, status }));

    // Call the PUT API via updateTriageFields thunk
    try {
      await dispatch(
        updateTriageFields(resolvedTicketId, {
          resolutionPath: resolutionPathDraft || ticket.resolutionPath || "",
          assignedTo: assignedToDraft || ticket.assignedTo || "Unassigned",
          incidentNumber: incidentNumberDraft || ticket.incidentId || "",
          status,
        }) as any
      );

      // Log activity
      dispatch(
        addTicketActivityComment({
          ticketId: resolvedTicketId,
          activity: {
            id: `a${Date.now()}`,
            authorInitials: "SY",
            authorName: "System",
            role: "Auto",
            time: nowAsTicketTime(),
            content: `Status changed to ${status}`,
            type: "system",
          },
        })
      );

      return { msg: "Status updated successfully", type: "success" };
    } catch (err: any) {
      // Revert optimistic update on failure
      dispatch(updateTicketStatus({ ticketId: resolvedTicketId, status: ticket.status as TicketStatus }));
      return { msg: err?.message || "Failed to update status", type: "error" };
    }
  }

  function changeUrgency(urgency: TicketUrgency) {
    dispatch(saveTicketDetailInfo(resolvedTicketId, { ...savedInfo, urgency }));
  }

  function setResolutionPath(value: string) {
    dispatch(setTriageInfo({ ticketId: resolvedTicketId, resolutionPath: value }));
  }

  function setAssignedTo(value: string) {
    dispatch(setTriageInfo({ ticketId: resolvedTicketId, assignedTo: value }));
  }

  function toggleWidespread(): ActionFeedback {
    const next = !isWidespread;
    setIsWidespreadState(next);
    dispatch(setTriageInfo({ ticketId: resolvedTicketId, isWidespread: next }));
    const msg = next
      ? "Flagged as widespread incident. Homepage banner activated."
      : "Widespread flag removed";
    const type = next ? "success" : "info";
    const activity = next
      ? "Ticket flagged as widespread incident. Homepage banner activated for affected locations."
      : "Widespread flag removed.";
    dispatch(
      addTicketActivityComment({
        ticketId: resolvedTicketId,
        activity: {
          id: `a${Date.now()}`,
          authorInitials: "ME",
          authorName: "You",
          role: "Sustain Team",
          time: nowAsTicketTime(),
          content: activity,
          type: "sustain",
        },
      })
    );

    // Fire-and-forget API call for widespread banner management
    const api = createApiClient();
    const clientTimestamp = getLocalTimestamp();
    const clientTimezone = getTimezoneOffsetString();
    if (next) {
      const params = new URLSearchParams({
        ticketNumber: resolvedTicketId,
        ticketTitle: ticket.title,
        performedBy: "sustain-team",
        clientTimezone,
      });
      api
        .post(`/ticketing/notifications/widespread?${params.toString()}`)
        .catch((err) => console.error("[toggleWidespread] widespread banner create failed:", err));
    } else {
      api
        .put(
          `/ticketing/notifications/widespread/${encodeURIComponent(resolvedTicketId)}/resolve?clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}`
        )
        .catch((err) => console.error("[toggleWidespread] widespread resolve failed:", err));
    }

    return { msg, type };
  }

  function handleMerge() {
    setIsMergeModalOpen(true);
  }

  function closeMergeModal() {
    setIsMergeModalOpen(false);
  }

  async function confirmMergeTickets(absorbedTicketRefs: string[]): Promise<ActionFeedback> {
    const nowMs = Date.now();
    
    // Keep modal open during submission; close only after success
    setIsMergeSubmitting(true);

    try {
      // Get numeric IDs: primaryTicket's backendTicketId and absorbed tickets' backendTicketIds from ticketDetails
      const primaryNumericId = ticket.backendTicketId;
      if (!primaryNumericId) {
        throw new Error("Primary ticket numeric ID not available. Please reload and try again.");
      }

      // Convert ticket references to numeric IDs by looking up in ticketDetails and sustainQueue
      const absorbedNumericIds: number[] = [];
      for (const ticketRef of absorbedTicketRefs) {
        let numericId: number | undefined;
        
        // Try ticketDetails first (for detailed tickets that have been loaded)
        if (ticketDetails[ticketRef]?.backendTicketId) {
          numericId = ticketDetails[ticketRef].backendTicketId;
        }
        // Fall back to sustainQueue (for tickets selected from search)
        else if (sustainQueue) {
          const queueEntry = sustainQueue.find(t => t.id === ticketRef);
          if (queueEntry?.backendId) {
            numericId = queueEntry.backendId;
          }
        }
        
        if (numericId) {
          absorbedNumericIds.push(numericId);
        } else {
          console.warn(`[confirmMergeTickets] Could not find numeric ID for absorbed ticket ${ticketRef}`);
        }
      }

      if (absorbedNumericIds.length === 0) {
        throw new Error("Could not resolve ticket IDs for merge. Please try again.");
      }

      // Call backend API to merge tickets using numeric IDs, passing requester email for email service auth
      // Also pass ticket references so backend can create clickable links in email notifications
      const result = await dispatch(mergeMultipleTickets(primaryNumericId, absorbedNumericIds, currentUserEmail, resolvedTicketId, absorbedTicketRefs));
      
      console.log('[confirmMergeTickets] API merge successful:', {
        parentId: primaryNumericId,
        childIds: absorbedNumericIds,
        result,
      });
      
      // **CRITICAL**: Fetch absorbed ticket details FIRST so consolidateMergedTickets can access their data
      // This ensures affected locations, attachments, and other data are available for consolidation
      console.log('[confirmMergeTickets] Fetching absorbed ticket details before consolidation...');
      for (const absorbedId of absorbedTicketRefs) {
        try {
          await dispatch(fetchTicketDetail(absorbedId, { setAsSelected: false, silent: true }) as any);
        } catch (err) {
          console.warn(`[confirmMergeTickets] Could not fetch absorbed ticket ${absorbedId}:`, err);
        }
      }
      
      // Store the absorbedTicketIds in a variable so we don't lose it after refetch
      const absorbedIdsForPersistence = absorbedTicketRefs;
      
      // Close modal after successful merge
      setIsMergeModalOpen(false);
      
      // **IMPORTANT**: Fetch primary ticket with small delay to let backend finish processing merge
      // This ensures we get the latest consolidated data from backend
      console.log('[confirmMergeTickets] Waiting for backend to process merge...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log('[confirmMergeTickets] Fetching fresh primary ticket details...');
      await dispatch(fetchTicketDetail(resolvedTicketId) as any);
      
      // **CRITICAL**: Apply consolidation ONLY ONCE after fetching fresh data from backend
      // This ensures affected locations and other consolidated data are preserved
      // and notifications are created only once
      console.log('[confirmMergeTickets] Applying consolidation after fresh fetch...');
      dispatch(consolidateMergedTickets({
        primaryTicketId: resolvedTicketId,
        absorbedTicketIds: absorbedIdsForPersistence,
        nowMs,
        performedBy: "Sustain Team Member",
      }));
      
      // Refresh all ticket lists to reflect merge status and consolidations
      console.log('[confirmMergeTickets] Refreshing ticket lists...');
      dispatch(fetchSustainQueue());
      
      // Also refresh myTickets in case the primary ticket is there
      // This ensures affected locations and other data are up to date
      dispatch(fetchMyTickets({}) as any);
      
      const count = absorbedNumericIds.length;
      return {
        msg: `Merge completed successfully. ${count} ticket${count > 1 ? "s" : ""} consolidated into ${resolvedTicketId}.`,
        type: "success",
      };
    } catch (error: any) {
      console.error('[confirmMergeTickets] Merge failed:', error);
      // Keep modal open so user can retry or adjust selections
      const errorMsg = error?.response?.data?.message 
        || error?.message 
        || "Failed to merge tickets. Please try again.";
      return {
        msg: errorMsg,
        type: "error",
      };
    } finally {
      setIsMergeSubmitting(false);
    }
  }

  function handleCreateADO() {
    setIsAdoModalOpen(true);
  }

  function closeAdoModal() {
    setIsAdoModalOpen(false);
  }

  async function confirmCreateAdoWorkItem(payload: {
    project: string;
    workItemType: string;
    title: string;
    reproStepsHtml: string;
    severity: string;
    priority: number;
    [key: string]: any;
  }): Promise<ActionFeedback> {
    try {
      const api = createApiClient();
      const clientTimestamp = getLocalTimestamp();
      
      // Build fields object - different for templates
      const fields: Record<string, any> = {
        "System.Title": payload.title,
        "Microsoft.VSTS.Common.Priority": payload.priority,
      };
      
      // Add description field based on work item type
      // User Story: use System.Description
      // Bug: use Microsoft.VSTS.TCM.ReproSteps
      const isUserStoryTemplate = payload.isReportingTemplate || payload.isSystemEnhancementNavigatorTemplate;
      const isBugTemplate = payload.isSystemIssueVisionPickTemplate || payload.isSystemIssueBugReviewTemplate;
      
      // Check if this is actually a bug work item (e.g., System Issue - Navigator, Bug Review, etc.)
      const isBugWorkItem = payload.workItemType === "Bug";
      
      if ((isUserStoryTemplate || payload.isSystemIssueNavigatorTemplate) && !isBugWorkItem) {
        // User Story: use System.Description
        fields["System.Description"] = payload.reproStepsHtml;
      } else if (isBugTemplate || isBugWorkItem) {
        // Bug: use ReproSteps field
        fields["Microsoft.VSTS.TCM.ReproSteps"] = payload.reproStepsHtml;
      }
      
      // Add template-specific fields for Reporting templates (New, Enhancement, Fix)
      if (payload.isReportingTemplate) {
        fields["Microsoft.VSTS.Common.AcceptanceCriteria"] = payload.acceptanceCriteria || "";
        fields["Custom.TargetDate"] = payload.targetDate || "";
        fields["System.AssignedTo"] = payload.assignedToAdo || "Emily Ruhala";
        fields["System.Tags"] = payload.tags || "New Report";
        if (payload.adoBoard) {
          fields["Custom.Board"] = payload.adoBoard;
        }
        // Add severity and priority for Reporting templates that create bugs (e.g., Reporting/Fix)
        if (payload.workItemType === "Bug") {
          const priorityLabelMap: Record<number, string> = {
            1: "1 - Critical", 2: "2 - High", 3: "3 - Medium", 4: "4 - Low",
          };
          fields["Microsoft.VSTS.Common.Severity"] = payload.severity;
          fields["Custom.B_Priority"] = priorityLabelMap[payload.priority] || `${payload.priority}`;
        }
      }
      
      // Add template-specific fields for System Issue Navigator and System Enhancement Navigator
      if (payload.isSystemIssueNavigatorTemplate || payload.isSystemEnhancementNavigatorTemplate) {
        fields["Microsoft.VSTS.Common.AcceptanceCriteria"] = payload.acceptanceCriteria || "";
      }
      
      // Add bug-specific fields for System Issue - Navigator (now creates Bug work items)
      if (payload.isSystemIssueNavigatorTemplate && payload.workItemType === "Bug") {
        const priorityLabelMap: Record<number, string> = {
          1: "1-Critical", 2: "2-High", 3: "3-Medium", 4: "4-Low",
        };
        fields["Microsoft.VSTS.Common.Severity"] = payload.severity;
        fields["Custom.DefectCategory"] = "Application";
        fields["Custom.DetectedOnEnvironment1"] = payload.environment || "Prod";
        fields["Custom.AssignedtoTeam11"] = "WSE Lighthouse";
        fields["Custom.B_Priority"] = priorityLabelMap[payload.priority] || `${payload.priority}`;
        fields["Custom.DetectedByPerson"] = currentUserEmail;
      }
      
      const isWESProject =
        ticket.category === "Vision Pick (WES)" &&
        ticket.subcategory === "System Issue";

      // Add template-specific fields for System Issue templates (Vision Pick, Bug Review)
      if (isBugTemplate) {
        const priorityLabelMap: Record<number, string> = {
          1: "1 - Critical", 2: "2 - High", 3: "3 - Medium", 4: "4 - Low",
        };
        fields["Microsoft.VSTS.Common.Severity"] = payload.severity;
        fields["Custom.DefectCategory"] = "Application";
        fields["Custom.DetectedOnEnvironment1"] = payload.environment || "Prod";
        fields["Custom.AssignedtoTeam11"] = "WSE Lighthouse";
        fields["Custom.B_Priority"] = priorityLabelMap[payload.priority] || `${payload.priority}`;
        fields["Custom.DetectedByPerson"] = currentUserEmail;
      }

      if (isWESProject) {
        fields["System.AreaPath"] = "WES\\Bug Review";
      }

      const response = await api.post(
        `/ado/work-items?clientTimestamp=${encodeURIComponent(clientTimestamp)}`,
        {
          ticketId: ticket.backendTicketId,  // CRITICAL: Send backend database ID as integer, not ticket number string
          project: isWESProject ? "WES" : "PBNA_WSE_LIGHTHOUSE",
          workItemType: payload.workItemType,
          fields,
          bypassRules: true,
          suppressNotifications: true,
        }
      );

      const backendWorkItemId: number | undefined =
        response?.data?.adoWorkItemId ?? response?.data?.workItemId ?? response?.data?.id;
      const adoWorkItemLabel = backendWorkItemId
        ? `ADO-${backendWorkItemId}`
        : `ADO-${Date.now()}`;

      setIsAdoModalOpen(false);

      // Add activity from backend response if available, otherwise fall back to locally created activity
      if (response?.data?.activityEntry) {
        const backendActivity = response.data.activityEntry;
        const activityItem: ActivityItem = {
          id: `a${backendActivity.id || Date.now()}`,
          authorInitials: backendActivity.authorInitials || "SY",
          authorName: backendActivity.authorName || "System",
          role: backendActivity.role || "Auto",
          time: backendActivity.time || "Just now",
          content: backendActivity.content,
          type: "system",
          ...(backendActivity.images ? { images: backendActivity.images } : {}),
        };
        dispatch(addTicketActivityComment({ ticketId: resolvedTicketId, activity: activityItem }));
      } else {
        // Fallback: create activity locally if backend didn't return it
        const nowForLog = new Date();
        const formattedDate = `${String(nowForLog.getMonth() + 1).padStart(2, "0")}/${String(nowForLog.getDate()).padStart(2, "0")}/${nowForLog.getFullYear()}`;
        const formattedTime = nowForLog.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
        const createdByName = currentUserEmail || "Sustain Team Member";
        dispatch(
          addTicketActivityComment({
            ticketId: resolvedTicketId,
            activity: {
              id: `a${Date.now()}`,
              authorInitials: "SY",
              authorName: "System",
              role: "Auto",
              time: "Just now",
              content: `ADO Work Item ${adoWorkItemLabel} created by ${createdByName} on ${formattedDate} at ${formattedTime}. Ticket moved to Submitted to ADO.`,
              type: "system",
            },
          })
        );
      }

      // Immediately sync activities with database to confirm ADO activity is persisted.
      // This ensures we have activity from BOTH response (immediate display) AND database (persistent storage).
      dispatch(fetchTicketActivities(resolvedTicketId) as any);

      // Fire the triage "Send Update" PUT with the ADO work item ID alongside current triage fields
      // so the backend stores the link in the same call.
      // Note: updateTriageFields will refetch activities, so we add our activity BEFORE calling it.
      // The backend refetch should now include the new ADO activity we just added.
      try {
        await dispatch(
          updateTriageFields(resolvedTicketId, {
            resolutionPath: resolutionPathDraft,
            assignedTo: assignedToDraft,
            incidentNumber: incidentNumberDraft,
            status: "submitted-ado",
            ...(backendWorkItemId !== undefined ? { adoWorkItemId: Number(backendWorkItemId) } : {}),
          }) as any
        );
      } catch (triageErr: any) {
        console.warn("[confirmCreateAdoWorkItem] triage update after ADO creation failed:", triageErr?.message);
      }

      // Optimistic local state update in case the thunk hasn't re-fetched yet
      dispatch(updateTicketStatus({ ticketId: resolvedTicketId, status: "submitted-ado" }));
      dispatch(setTriageInfo({ ticketId: resolvedTicketId, adoId: adoWorkItemLabel }));

      return { msg: `${adoWorkItemLabel} created and linked. Ticket moved to Submitted to ADO.`, type: "success" };
    } catch (err: any) {
      console.warn("[confirmCreateAdoWorkItem] failed:", err?.message);
      return {
        msg: err?.response?.data?.message || err?.message || "Failed to create ADO work item.",
        type: "error",
      };
    }
  }

  function handleManageImpact(): ActionFeedback {
    return { msg: "Affected locations updated", type: "success" };
  }

  function addImpactLocation(name: string, locId: string, sapId: string) {
    // Optimistic local update so the UI reflects the new location immediately
    dispatch(
      addAffectedLocation({
        ticketId: resolvedTicketId,
        location: {
          id: `loc-${Date.now()}`,
          name,
          sapId,
          time: nowAsTicketTime(),
        },
      })
    );
    dispatch(
      addTicketActivityComment({
        ticketId: resolvedTicketId,
        activity: {
          id: `a${Date.now()}`,
          authorInitials: "SY",
          authorName: "System",
          role: "Auto",
          time: nowAsTicketTime(),
          content: `Added ${name} to affected locations.`,
          type: "system",
        },
      })
    );

    // Fire-and-forget API call — refresh ticket detail on completion
    dispatch(
      addAffectedLocationToTicket(
        resolvedTicketId,
        name,
        locId,
        sapId || null,
        currentUserEmail,
      ) as any
    ).catch((err: any) =>
      console.error("[addImpactLocation] API call failed:", err?.message)
    );
  }

  async function removeImpactLocation(locationId: string) {
    const loc = ticket.affectedLocations.find((x) => x.id === locationId);
    if (!loc) return;

    try {
      await dispatch(removeAffectedLocationFromTicket(resolvedTicketId, loc.name) as any);
    } catch (err: any) {
      console.error("[removeImpactLocation] API call failed:", err?.message);
      return;
    }

    dispatch(removeAffectedLocation({ ticketId: resolvedTicketId, locationId }));
    dispatch(
      addTicketActivityComment({
        ticketId: resolvedTicketId,
        activity: {
          id: `a${Date.now()}`,
          authorInitials: "SY",
          authorName: "System",
          role: "Auto",
          time: nowAsTicketTime(),
          content: `Removed ${loc.name} from affected locations.`,
          type: "system",
        },
      })
    );
  }

  function addSecondaryStakeholder(email: string) {
    const trimmed = email.trim();
    if (!trimmed) return;

    // Optimistic local update so the UI reflects the new stakeholder immediately
    dispatch(addTicketStakeholderEmail({ ticketId: resolvedTicketId, email: trimmed }));
    dispatch(
      addTicketActivityComment({
        ticketId: resolvedTicketId,
        activity: {
          id: `a${Date.now()}`,
          authorInitials: "SY",
          authorName: "System",
          role: "Auto",
          time: nowAsTicketTime(),
          content: `Added ${trimmed} as a secondary stakeholder.`,
          type: "system",
        },
      })
    );

    if (ticket.backendTicketId != null) {
      dispatch(addTicketDetailStakeholder(resolvedTicketId, ticket.backendTicketId, trimmed) as any).catch(
        (err: any) => console.error("[addSecondaryStakeholder] API call failed:", err?.message)
      );
    }
  }

  function handleResolve(): ActionFeedback {
    const wasWidespread = isWidespread;
    dispatch(updateTicketStatus({ ticketId: resolvedTicketId, status: "resolved" }));
    dispatch(
      addTicketActivityComment({
        ticketId: resolvedTicketId,
        activity: {
          id: `a${Date.now()}`,
          authorInitials: "SY",
          authorName: "System",
          role: "Auto",
          time: nowAsTicketTime(),
          content: "Status changed to Closed.",
          type: "system",
        },
      })
    );
    if (wasWidespread) {
      // Fire-and-forget API call to resolve the widespread banner
      const api = createApiClient();
      const clientTimestamp = getLocalTimestamp();
      const clientTimezone = getTimezoneOffsetString();
      api
        .put(
          `/ticketing/notifications/widespread/${encodeURIComponent(resolvedTicketId)}/resolve?clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}`
        )
        .catch((err) => console.error("[handleResolve] widespread resolve failed:", err));
      return { msg: "Widespread issue resolved. Banner removed.", type: "success" };
    }
    return { msg: "Ticket closed. ServiceNow will close within 5 min.", type: "success" };
  }

  async function handleCloseTicket(
    catId: number,
    subCatId: number,
    rcaCategory: string,
    rcaNotes: string,
    resolution: string,
    resolvedBy: string
  ): Promise<ActionFeedback> {
    const wasWidespread = isWidespread;

    const result = await closeTicketWithRca({
      dispatch,
      ticketId: resolvedTicketId,
      meta: {
        categoryId: catId,
        subCategoryId: subCatId,
        rcaCategory,
        rcaNotes,
        resolution,
        resolvedBy,
      },
      performCloseUpdate: async (meta) => {
        const effectiveAssignedTo = assignedToDraft || ticket.assignedTo || "Unassigned";
        await dispatch(
          updateTriageFields(resolvedTicketId, {
            resolutionPath: resolutionPathDraft || ticket.resolutionPath || "",
            assignedTo: effectiveAssignedTo,
            incidentNumber: incidentNumberDraft || ticket.incidentId || "",
            status: "resolved",
            categoryId: meta.categoryId,
            subCategoryId: meta.subCategoryId,
            rcaCategory: meta.rcaCategory,
            resolvedBy: meta.resolvedBy,
          }) as any
        );
        await refreshTeams();
      },
    });

    if (result.type !== "success") {
      console.error("[handleCloseTicket] Failed to close ticket:", result.msg);
      return result;
    }

    if (wasWidespread) {
      // Fire-and-forget API call to resolve the widespread banner
      const api = createApiClient();
      const clientTimestamp = getLocalTimestamp();
      const clientTimezone = getTimezoneOffsetString();
      api
        .put(
          `/ticketing/notifications/widespread/${encodeURIComponent(resolvedTicketId)}/resolve?clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}`
        )
        .catch((err) => console.error("[handleCloseTicket] widespread resolve failed:", err));
    }

    // Re-fetch activities & notes to stay synced
    dispatch(fetchTicketDetail(resolvedTicketId) as any);
    dispatch(fetchTicketActivities(resolvedTicketId) as any);
    dispatch(fetchTicketNotes(resolvedTicketId) as any);

    return result;
  }

  function handleTriageResolutionPathChange(value: string) {
    setResolutionPathDraft(value);
    // Clear incident number if switching away from "Requires IT"
    if (value !== "Requires IT" && incidentNumberDraft) {
      setIncidentNumberDraft("");
    }
  }

  function handleTriageAssignedToChange(value: string) {
    setAssignedToDraft(value);
  }

  function handleIncidentNumberChange(value: string) {
    setIncidentNumberDraft(value);
  }

  async function handleSendTriageUpdate(incidentNumberOverride?: string): Promise<ActionFeedback> {
    setIsTriageSending(true);
    try {
      const effectiveIncidentNumber = incidentNumberOverride ?? incidentNumberDraft;

      // Detect what changed
      const resolutionPathChanged = resolutionPathDraft !== (ticket.resolutionPath ?? "");
      const assignedToChanged = assignedToDraft !== (ticket.assignedTo ?? "Unassigned");
      const incidentNumberChanged = effectiveIncidentNumber !== (ticket.incidentId ?? "");

      if (!resolutionPathChanged && !assignedToChanged && !incidentNumberChanged) {
        setIsTriageSending(false);
        return { msg: "No changes to send", type: "info" };
      }

      // Determine new status: if assigning and current status is "New", upgrade to "In Progress"
      let newStatus = ticket.status;
      if (assignedToChanged && assignedToDraft !== "Unassigned" && ticket.status === "new") {
        newStatus = "progress";
      }

      // Call thunk to update the ticket
      await dispatch(
        updateTriageFields(resolvedTicketId, {
          resolutionPath: resolutionPathDraft,
          assignedTo: assignedToDraft,
          incidentNumber: effectiveIncidentNumber,
          status: newStatus,
        }) as any
      );
      await refreshTeams();

      // Explicitly refresh Activity panel from activities endpoint after PUT Send Update.
      await dispatch(fetchTicketActivities(resolvedTicketId) as any);

      if (incidentNumberOverride !== undefined) {
        setIncidentNumberDraft(effectiveIncidentNumber);
      }

      setIsTriageSending(false);
      return { msg: "Triage information updated successfully", type: "success" };
    } catch (error: any) {
      setIsTriageSending(false);
      console.error("Error sending triage update:", error);
      return { msg: error?.message || "Failed to send triage update", type: "error" };
    }
  }

  function hasTriageChanges(): boolean {
    return (
      resolutionPathDraft !== (ticket.resolutionPath ?? "") ||
      assignedToDraft !== (ticket.assignedTo ?? "Unassigned") ||
      incidentNumberDraft !== (ticket.incidentId ?? "")
    );
  }

  const assignedTeam = useMemo(
    () => teams.find((t) => t.id === ticket.assignedTeamId) ?? null,
    [teams, ticket.assignedTeamId]
  );
  const assignedTeamName = assignedTeam?.name ?? null;
  const isAssignedTeamInactive = Boolean(assignedTeam && !(assignedTeam.isActive ?? assignedTeam.active ?? true));

  async function changeAssignedTeam(teamId: number): Promise<ActionFeedback> {
    setIsReassigningTeam(true);
    try {
      await dispatch(updateAssignedTeam(resolvedTicketId, teamId) as any);
      await refreshTeams();
      await dispatch(fetchTicketDetail(resolvedTicketId, { setAsSelected: false, silent: true }) as any);
      return { msg: "Team assignment updated.", type: "success" };
    } catch (error: any) {
      return {
        msg: error?.response?.data?.message || error?.message || "Failed to update team assignment.",
        type: "error",
      };
    } finally {
      setIsReassigningTeam(false);
    }
  }

  const locationsToShow = showAllLocations
    ? ticket.affectedLocations
    : ticket.affectedLocations.slice(0, 6);
  const hasMoreLocations = ticket.affectedLocations.length > 6;

  return {
    ticket,
    savedInfo,
    isEditingInfo,
    editDraft,
    editErrors,
    commentText,
    noteText,
    notes: ticket.notes,
    activity: ticket.activity,
    locationsToShow,
    impactedPickersCount,
    hasMoreLocations,
    showAllLocations,
    resolutionPath: resolutionPathDraft,
    assignedTo: assignedToDraft,
    incidentNumber: incidentNumberDraft,
    isWidespread,
    isMergeModalOpen,
    isAdoModalOpen,
    isTriageSending,
    startEdit,
    cancelEdit,
    saveEdit,
    patchDraft,
    postComment: submitComment,
    postCommentFull: submitCommentFull,
    saveNote: submitNote,
    saveNoteFull,
    setCommentText,
    setNoteText,
    setShowAllLocations,
    changeStatus,
    setResolutionPath: handleTriageResolutionPathChange,
    setAssignedTo: handleTriageAssignedToChange,
    setIncidentNumber: handleIncidentNumberChange,
    toggleWidespread,
    addImpactLocation,
    removeImpactLocation,
    addSecondaryStakeholder,
    handleMerge,
    closeMergeModal,
    confirmMergeTickets,
    handleCreateADO,
    closeAdoModal,
    confirmCreateAdoWorkItem,
    handleManageImpact,
    handleResolve,
    handleCloseTicket,
    handleSendTriageUpdate,
    hasTriageChanges,
    changeUrgency,
    assignedTeams: teams,
    assignedTeamName,
    isAssignedTeamInactive,
    isReassigningTeam,
    changeAssignedTeam,
  };
}
